import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const AuditConfigTab = () => {
  const [auditSettings, setAuditSettings] = useState({
    auditFrequency: "quarterly",
    retentionPeriod: "7",
    retentionUnit: "years",
    autoBackup: true,
    backupFrequency: "daily",
    complianceStandard: "sox",
    requireApproval: true,
    auditTrailEnabled: true,
    encryptionEnabled: true,
    anonymizeData: false
  });

  const [complianceTemplates, setComplianceTemplates] = useState([
    {
      id: 1,
      name: "SOX Compliance Template",
      standard: "SOX",
      description: "Sarbanes-Oxley Act compliance requirements for financial reporting",
      requirements: ["Asset valuation tracking", "Change management logs", "Access control records"],
      isActive: true,
      lastUpdated: "2025-08-15"
    },
    {
      id: 2,
      name: "GDPR Data Protection",
      standard: "GDPR",
      description: "General Data Protection Regulation compliance for personal data",
      requirements: ["Data retention policies", "User consent tracking", "Data anonymization"],
      isActive: true,
      lastUpdated: "2025-08-10"
    },
    {
      id: 3,
      name: "ISO 27001 Security",
      standard: "ISO 27001",
      description: "Information security management system requirements",
      requirements: ["Security incident logs", "Asset classification", "Risk assessments"],
      isActive: false,
      lastUpdated: "2025-07-20"
    }
  ]);

  const [backupStatus, setBackupStatus] = useState({
    lastBackup: "2025-08-19T04:00:00Z",
    backupSize: "2.4 GB",
    status: "success",
    nextScheduled: "2025-08-20T04:00:00Z"
  });

  const [isModified, setIsModified] = useState(false);

  const auditFrequencyOptions = [
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
    { value: "quarterly", label: "Quarterly" },
    { value: "semi_annual", label: "Semi-Annual" },
    { value: "annual", label: "Annual" }
  ];

  const retentionUnitOptions = [
    { value: "months", label: "Months" },
    { value: "years", label: "Years" }
  ];

  const backupFrequencyOptions = [
    { value: "hourly", label: "Every Hour" },
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" }
  ];

  const complianceStandardOptions = [
    { value: "sox", label: "SOX (Sarbanes-Oxley)" },
    { value: "gdpr", label: "GDPR (Data Protection)" },
    { value: "iso27001", label: "ISO 27001 (Security)" },
    { value: "hipaa", label: "HIPAA (Healthcare)" },
    { value: "custom", label: "Custom Standard" }
  ];

  const handleSettingChange = (field, value) => {
    setAuditSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setIsModified(true);
  };

  const handleSaveSettings = () => {
    console.log('Saving audit configuration:', auditSettings);
    setIsModified(false);
  };

  const handleRunBackup = async () => {
    console.log('Starting manual backup...');
    setBackupStatus(prev => ({ ...prev, status: 'running' }));
    
    // Simulate backup process
    await new Promise(resolve => setTimeout(resolve, 3000));
    
    setBackupStatus({
      lastBackup: new Date()?.toISOString(),
      backupSize: "2.5 GB",
      status: "success",
      nextScheduled: new Date(Date.now() + 24 * 60 * 60 * 1000)?.toISOString()
    });
  };

  const handleToggleTemplate = (id) => {
    setComplianceTemplates(prev =>
      prev?.map(template =>
        template?.id === id ? { ...template, isActive: !template?.isActive } : template
      )
    );
  };

  const formatDateTime = (timestamp) => {
    return new Date(timestamp)?.toLocaleString();
  };

  const getBackupStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'text-success';
      case 'running':
        return 'text-accent';
      case 'error':
        return 'text-error';
      default:
        return 'text-muted-foreground';
    }
  };

  const getBackupStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return 'CheckCircle';
      case 'running':
        return 'Clock';
      case 'error':
        return 'AlertCircle';
      default:
        return 'Clock';
    }
  };

  return (
    <div className="space-y-8">
      {/* Audit Schedule Settings */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center space-x-2 mb-6">
          <Icon name="Calendar" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-card-foreground">Audit Schedule Configuration</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Select
            label="Audit Frequency"
            description="How often should audits be conducted"
            options={auditFrequencyOptions}
            value={auditSettings?.auditFrequency}
            onChange={(value) => handleSettingChange('auditFrequency', value)}
          />
          
          <div className="flex space-x-2">
            <Input
              label="Data Retention Period"
              type="number"
              value={auditSettings?.retentionPeriod}
              onChange={(e) => handleSettingChange('retentionPeriod', e?.target?.value)}
              className="flex-1"
            />
            <Select
              label="Unit"
              options={retentionUnitOptions}
              value={auditSettings?.retentionUnit}
              onChange={(value) => handleSettingChange('retentionUnit', value)}
              className="w-32"
            />
          </div>
          
          <Select
            label="Compliance Standard"
            options={complianceStandardOptions}
            value={auditSettings?.complianceStandard}
            onChange={(value) => handleSettingChange('complianceStandard', value)}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <div className="space-y-4">
            <Checkbox
              label="Require Approval for Changes"
              description="All audit configuration changes need administrator approval"
              checked={auditSettings?.requireApproval}
              onChange={(e) => handleSettingChange('requireApproval', e?.target?.checked)}
            />
            
            <Checkbox
              label="Enable Audit Trail"
              description="Track all user actions and system changes"
              checked={auditSettings?.auditTrailEnabled}
              onChange={(e) => handleSettingChange('auditTrailEnabled', e?.target?.checked)}
            />
          </div>
          
          <div className="space-y-4">
            <Checkbox
              label="Enable Data Encryption"
              description="Encrypt audit logs and backup files"
              checked={auditSettings?.encryptionEnabled}
              onChange={(e) => handleSettingChange('encryptionEnabled', e?.target?.checked)}
            />
            
            <Checkbox
              label="Anonymize Personal Data"
              description="Remove personally identifiable information from logs"
              checked={auditSettings?.anonymizeData}
              onChange={(e) => handleSettingChange('anonymizeData', e?.target?.checked)}
            />
          </div>
        </div>
      </div>
      {/* Backup Configuration */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center space-x-2 mb-6">
          <Icon name="Database" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-card-foreground">Backup Configuration</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Checkbox
              label="Enable Automatic Backup"
              description="Automatically backup audit data and configurations"
              checked={auditSettings?.autoBackup}
              onChange={(e) => handleSettingChange('autoBackup', e?.target?.checked)}
            />
            
            <Select
              label="Backup Frequency"
              options={backupFrequencyOptions}
              value={auditSettings?.backupFrequency}
              onChange={(value) => handleSettingChange('backupFrequency', value)}
              disabled={!auditSettings?.autoBackup}
            />
          </div>
          
          <div className="bg-muted/50 rounded-lg p-4">
            <h4 className="font-medium text-card-foreground mb-3">Backup Status</h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Last Backup:</span>
                <div className={`flex items-center space-x-1 ${getBackupStatusColor(backupStatus?.status)}`}>
                  <Icon name={getBackupStatusIcon(backupStatus?.status)} size={14} />
                  <span>{formatDateTime(backupStatus?.lastBackup)}</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Backup Size:</span>
                <span className="font-medium text-card-foreground">{backupStatus?.backupSize}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Next Scheduled:</span>
                <span className="font-medium text-card-foreground">{formatDateTime(backupStatus?.nextScheduled)}</span>
              </div>
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={handleRunBackup}
              disabled={backupStatus?.status === 'running'}
              loading={backupStatus?.status === 'running'}
              iconName="Download"
              iconPosition="left"
              className="w-full mt-4"
            >
              Run Manual Backup
            </Button>
          </div>
        </div>
      </div>
      {/* Compliance Templates */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <Icon name="FileText" size={20} className="text-primary" />
            <h3 className="text-lg font-semibold text-card-foreground">Compliance Templates</h3>
          </div>
          <Button variant="outline" size="sm" iconName="Plus" iconPosition="left">
            Add Template
          </Button>
        </div>
        
        <div className="space-y-4">
          {complianceTemplates?.map((template) => (
            <div key={template?.id} className="border border-border rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start space-x-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold ${
                    template?.isActive ? 'bg-primary' : 'bg-muted-foreground'
                  }`}>
                    {template?.standard?.substring(0, 2)}
                  </div>
                  <div>
                    <h4 className="font-semibold text-card-foreground">{template?.name}</h4>
                    <p className="text-sm text-muted-foreground">{template?.description}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                    template?.isActive 
                      ? 'bg-success/10 text-success' :'bg-muted text-muted-foreground'
                  }`}>
                    {template?.isActive ? 'Active' : 'Inactive'}
                  </div>
                </div>
              </div>
              
              <div className="mb-3">
                <p className="text-sm text-muted-foreground mb-2">Requirements:</p>
                <div className="flex flex-wrap gap-1">
                  {template?.requirements?.map((req, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded"
                    >
                      {req}
                    </span>
                  ))}
                </div>
              </div>
              
              <div className="flex items-center justify-between pt-3 border-t border-border">
                <span className="text-sm text-muted-foreground">
                  Last updated: {template?.lastUpdated}
                </span>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    checked={template?.isActive}
                    onChange={() => handleToggleTemplate(template?.id)}
                    label="Enabled"
                  />
                  <Button variant="ghost" size="sm" iconName="Edit" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-6 border-t border-border">
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Icon name="Clock" size={16} />
          <span>Configuration last updated: Aug 19, 2025 at 4:36 AM</span>
        </div>
        
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            onClick={() => {
              setAuditSettings({
                auditFrequency: "quarterly",
                retentionPeriod: "7",
                retentionUnit: "years",
                autoBackup: true,
                backupFrequency: "daily",
                complianceStandard: "sox",
                requireApproval: true,
                auditTrailEnabled: true,
                encryptionEnabled: true,
                anonymizeData: false
              });
              setIsModified(false);
            }}
            disabled={!isModified}
          >
            Reset Changes
          </Button>
          <Button
            variant="default"
            onClick={handleSaveSettings}
            disabled={!isModified}
            iconName="Save"
            iconPosition="left"
          >
            Save Configuration
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AuditConfigTab;